/**
 * LibraryBrowser — admin view of the raw video files under the server's
 * MEDIA_DIR (the downloaded movies/TV). Each file shows whether it is already in
 * the syncreel library; "Add" registers it and queues the transcode (video,
 * audio, embedded + sidecar subtitles). Removing a title is done from the
 * catalogue below, which deletes it from syncreel but never touches the source.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LibraryFile, MediaStatus } from '@syncreel/shared';
import { addLibraryFile, getLibraryFiles } from '../api/client';
import { useSessionStore } from '../store/session';
import { Badge, type BadgeVariant } from './ui/Badge';
import { Button } from './ui/Button';
import { Spinner } from './ui/Spinner';
import { useToast } from './ui/Toast';
import styles from './LibraryBrowser.module.css';

export interface LibraryBrowserProps {
  /** Called after a file was added (or re-queued) so the catalogue can reload. */
  onChanged: () => void;
}

/** Format a byte count as MB/GB. */
function formatSize(bytes: number): string {
  const gb = bytes / 1024 ** 3;
  return gb >= 1 ? `${gb.toFixed(2)} GB` : `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
}

/** Badge for a registered file's status. */
function statusBadge(status: MediaStatus): { variant: BadgeVariant; label: string } {
  switch (status) {
    case 'ready':
      return { variant: 'ok', label: 'In library' };
    case 'transcoding':
      return { variant: 'info', label: 'Transcoding…' };
    case 'pending':
      return { variant: 'neutral', label: 'Queued' };
    case 'failed':
      return { variant: 'error', label: 'Failed' };
  }
}

/** Collapsible list of server files with an Add button each. */
export function LibraryBrowser({ onChanged }: LibraryBrowserProps) {
  const toast = useToast();
  const adminToken = useSessionStore((s) => s.adminToken);
  const [open, setOpen] = useState<boolean>(false);
  const [files, setFiles] = useState<LibraryFile[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState<string>('');
  const [onlyNew, setOnlyNew] = useState<boolean>(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const res = await getLibraryFiles(adminToken);
      setFiles(res.files);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to list server files.');
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  async function handleAdd(file: LibraryFile) {
    if (!adminToken) return;
    setBusy(file.relPath);
    try {
      const res = await addLibraryFile(file.relPath, adminToken);
      toast.success(res.alreadyAdded ? `Re-queued "${file.name}".` : `Added "${file.name}" — transcoding started.`);
      await load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add file.');
    } finally {
      setBusy(null);
    }
  }

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return files.filter(
      (f) => (!onlyNew || f.mediaId === null || f.status === 'failed') && (q === '' || f.relPath.toLowerCase().includes(q)),
    );
  }, [files, query, onlyNew]);

  return (
    <div className={styles.browser}>
      <div className={styles.header}>
        <Button variant="secondary" size="sm" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? 'Hide server library' : 'Add from server library'}
        </Button>
        {open ? (
          <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
        ) : null}
      </div>
      {open ? (
        <>
          <div className={styles.filters}>
            <input
              className={styles.search}
              type="search"
              placeholder="Filter by name or folder…"
              value={query}
              maxLength={100}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Filter server files"
            />
            <label className={styles.check}>
              <input type="checkbox" checked={onlyNew} onChange={(e) => setOnlyNew(e.target.checked)} />
              Only not-yet-added
            </label>
          </div>
          {loading && files.length === 0 ? (
            <Spinner label="Scanning server library…" />
          ) : error ? (
            <p className={styles.empty}>{error}</p>
          ) : visible.length === 0 ? (
            <p className={styles.empty}>{files.length === 0 ? 'No video files found.' : 'Nothing matches.'}</p>
          ) : (
            <ul className={styles.list}>
              {visible.map((f) => {
                const added = f.mediaId !== null && f.status !== 'failed';
                const badge = f.status !== null ? statusBadge(f.status) : null;
                return (
                  <li key={f.relPath} className={styles.row}>
                    <div className={styles.info}>
                      <span className={styles.name} title={f.relPath}>
                        {f.relPath}
                      </span>
                      <span className={styles.meta}>
                        {formatSize(f.sizeBytes)}
                        {f.subtitleCount > 0 ? ` · ${f.subtitleCount} subtitle file${f.subtitleCount > 1 ? 's' : ''}` : ''}
                      </span>
                    </div>
                    {badge ? <Badge variant={badge.variant} label={badge.label} /> : null}
                    {!added ? (
                      <Button
                        size="sm"
                        loading={busy === f.relPath}
                        disabled={busy !== null}
                        onClick={() => void handleAdd(f)}
                      >
                        {f.status === 'failed' ? 'Retry' : 'Add'}
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      ) : null}
    </div>
  );
}
